import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Form, Input, DatePicker, Tooltip, Upload, Tag, message } from 'antd';
import { Button } from 'primereact/button';
import { Image } from 'primereact/image';
import useIsMobile from '../../utils/mediaQuery';
import {
  usePostAssetMutation,
  useGetAssetPhotoUploadUrlMutation,
  useDeleteAssetPhotoMutation,
} from '../../features/api/apiSlice';
import { InboxOutlined } from '@ant-design/icons';

import { createPhotoObject, getPhotoDisplayUrl } from '../../utils/photoUtilities';
import { uploadPhotoViaPresignedUrl, resolveUploadBody } from '../../services/s3PhotoUpload';
import { v4 as uuidv4 } from 'uuid';

const generateDraftKey = () => {
  return uuidv4();
};

const PHOTO_STATUS = {
  UPLOADING: 'uploading',
  UPLOADED: 'uploaded',
  ERROR: 'error',
  DELETING: 'deleting',
};

const STATUS_STYLES = {
  [PHOTO_STATUS.UPLOADING]: { color: 'gold', label: 'Uploading' },
  [PHOTO_STATUS.UPLOADED]: { color: 'green', label: 'Uploaded' },
  [PHOTO_STATUS.ERROR]: { color: 'red', label: 'Error' },
  [PHOTO_STATUS.DELETING]: { color: 'orange', label: 'Removing' },
};

export default function AddAssetForm({
  open,
  setOpen,
  projectId,
  currentAsset,
  updateCurrentAsset,
  initialPhoto,
  parentPpkLoading,
  parentPpkPosition,
}) {
  const CURRENT_SCREEN = 'ASSETS_PAGE';
  const [form] = Form.useForm();
  const [postAsset, { isLoading }] = usePostAssetMutation();
  const [getAssetPhotoUploadUrl] = useGetAssetPhotoUploadUrlMutation();
  const [deleteAssetPhoto] = useDeleteAssetPhotoMutation();
  const isMobile = useIsMobile();
  const [photosMeta, setPhotosMeta] = useState([]);
  const [draftKey, setDraftKey] = useState(() => generateDraftKey());
  const [submitting, setSubmitting] = useState(false);
  const [ppkLoading, setPpkLoading] = useState(false);
  const [hasFormErrors, setHasFormErrors] = useState(false);
  const photosMetaRef = useRef([]);
  const hasSubmittedRef = useRef(false);
  const enqueuePhotoUploadRef = useRef(null);
  const hasUploadingPhotos = photosMeta.some((photo) => photo.status === PHOTO_STATUS.UPLOADING);
  const hasErroredPhotos = photosMeta.some((photo) => photo.status === PHOTO_STATUS.ERROR);

  useEffect(() => {
    photosMetaRef.current = photosMeta;
  }, [photosMeta]);

  const performUpload = useCallback(
    async (tempId, photoData, overrideDraftKey = null) => {
      const activeDraftKey = overrideDraftKey || draftKey;
      if (!activeDraftKey) return;

      try {
        const { body, contentType } = resolveUploadBody(photoData);
        const { id } = await uploadPhotoViaPresignedUrl({
          requestUploadUrl: (payload) => getAssetPhotoUploadUrl({ projectId, payload }).unwrap(),
          body,
          contentType,
          uploadPayload: {
            draft_key: activeDraftKey,
            mime_type: contentType,
            file_name: photoData.fileName || photoData.name || 'Photo',
            file_size: photoData.fileSize || (body.size ?? null),
          },
        });

        setPhotosMeta((prev) =>
          prev.map((photo) =>
            photo.tempId === tempId
              ? {
                  ...photo,
                  id,
                  status: PHOTO_STATUS.UPLOADED,
                  uploadStatus: PHOTO_STATUS.UPLOADED,
                }
              : photo
          )
        );
      } catch (error) {
        console.error('Failed to upload photo', error);
        setPhotosMeta((prev) =>
          prev.map((photo) =>
            photo.tempId === tempId
              ? {
                  ...photo,
                  status: PHOTO_STATUS.ERROR,
                  errorMessage: error?.data?.error || error?.message || 'Failed to upload photo',
                }
              : photo
          )
        );
      }
    },
    [draftKey, projectId, getAssetPhotoUploadUrl]
  );

  const enqueuePhotoUpload = useCallback(
    (photoData, options = {}) => {
      if (!photoData?.file && !photoData?.base64 && !photoData?.url) {
        message.error('Missing photo data.');
        return;
      }

      const tempId = options.tempId || `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
      const fileType = photoData.fileType || photoData.mime_type || 'image/jpeg';
      const previewUrl =
        photoData.previewUrl ||
        (photoData.file ? URL.createObjectURL(photoData.file) : null) ||
        (photoData.base64
          ? photoData.base64.startsWith('data:')
            ? photoData.base64
            : `data:${fileType};base64,${photoData.base64}`
          : null);
      const entry = {
        tempId,
        id: photoData.id || null,
        file: photoData.file || null,
        base64: photoData.base64 || null,
        previewUrl,
        url: photoData.url || null,
        fileName: photoData.fileName || photoData.name || 'Photo',
        fileType,
        fileSize: photoData.fileSize || null,
        location: photoData.location || null,
        status: photoData.id ? PHOTO_STATUS.UPLOADED : PHOTO_STATUS.UPLOADING,
        errorMessage: null,
      };

      setPhotosMeta((prev) => (options.prepend ? [entry, ...prev] : [...prev, entry]));

      if (!photoData.id) {
        void performUpload(tempId, entry, options.overrideDraftKey);
      }
    },
    [performUpload]
  );

  useEffect(() => {
    enqueuePhotoUploadRef.current = enqueuePhotoUpload;
  }, [enqueuePhotoUpload]);

  const retryPhotoUpload = useCallback(
    (tempId) => {
      const target = photosMetaRef.current.find((photo) => photo.tempId === tempId);
      if (!target) return;

      setPhotosMeta((prev) =>
        prev.map((photo) =>
          photo.tempId === tempId
            ? { ...photo, status: PHOTO_STATUS.UPLOADING, errorMessage: null }
            : photo
        )
      );

      void performUpload(tempId, target);
    },
    [performUpload]
  );

  const cleanupDraftUploads = useCallback(
    async (photoList) => {
      const deletable = (photoList || [])
        .filter(
          (photo) =>
            photo.id &&
            (photo.status === PHOTO_STATUS.UPLOADED || photo.status === PHOTO_STATUS.DELETING)
        )
        .map((photo) => photo.id);

      if (!deletable.length) return;

      await Promise.all(
        deletable.map((photoId) =>
          deleteAssetPhoto({ projectId, photoId })
            .unwrap()
            .catch((error) => {
              console.error('Failed to delete staged photo', photoId, error);
            })
        )
      );
    },
    [deleteAssetPhoto, projectId]
  );

  const openCamera = () =>
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: 'TRIGGER-CAMERA', screen: CURRENT_SCREEN })
    );
  const openGallery = () =>
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: 'TRIGGER-GALLERY', screen: CURRENT_SCREEN })
    );
  const clearPhotos = () =>
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: 'CLEAR-PHOTOS', screen: CURRENT_SCREEN })
    );
  const deletePhoto = (idx) =>
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: 'DELETE-PHOTO', index: idx, screen: CURRENT_SCREEN })
    );
  const requestPPK = () =>
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'REQUEST-PPK' }));
  const requestGeolocation = () =>
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'REQUEST-GEOLOCATION' }));

  useEffect(() => {
    if (open) {
      const newDraftKey = generateDraftKey();
      hasSubmittedRef.current = false;
      setDraftKey(newDraftKey);
      setPhotosMeta([]);
      setHasFormErrors(false);
      console.log('initialPhoto', initialPhoto);

      form.resetFields();
      // Check for form errors after setting initial values
      setTimeout(() => {
        const fieldsError = form.getFieldsError();
        const hasValidationErrors = fieldsError.some((field) => field.errors.length > 0);

        const values = form.getFieldsValue();
        const hasMissingRequiredFields = !values.name || !values.installed_at;

        setHasFormErrors(hasValidationErrors || hasMissingRequiredFields);
      }, 0);
      updateCurrentAsset('lngLat', [0, 0]);
      clearPhotos();

      if ((initialPhoto?.base64 || initialPhoto?.file) && enqueuePhotoUploadRef.current) {
        enqueuePhotoUploadRef.current(
          {
            ...createPhotoObject({ ...initialPhoto, isExisting: false }),
            base64: initialPhoto.base64 || null,
            location: initialPhoto.location || null,
          },
          { overrideDraftKey: newDraftKey, prepend: true }
        );
      }
    }

    return () => {
      if (!hasSubmittedRef.current) {
        cleanupDraftUploads(photosMetaRef.current);
      }
      setPhotosMeta([]);
      updateCurrentAsset('lngLat', [0, 0]);
      clearPhotos();
    };
  }, [open, form, updateCurrentAsset, initialPhoto, cleanupDraftUploads]);

  useEffect(() => {
    if (!parentPpkLoading && !ppkLoading && parentPpkPosition) {
      const pos = parentPpkPosition;
      const latitude = pos.latitude || pos.optDouble?.('latitude');
      const longitude = pos.longitude || pos.optDouble?.('longitude');
      const height = pos.height || pos.optDouble?.('height');
      form.setFieldsValue({
        latitude: latitude,
        longitude: longitude,
        height: height || undefined,
      });
      if (latitude && longitude) {
        updateCurrentAsset('lngLat', [longitude, latitude]);
        updateCurrentAsset('ppkData', {
          latitude,
          longitude,
          height,
          quality: pos.quality || pos.optInt?.('quality'),
          quality_label: pos.quality_label || pos.optString?.('quality_label', 'Unknown'),
          satellites: pos.satellites || pos.optInt?.('satellites'),
        });
      }
    }
  }, [parentPpkLoading, parentPpkPosition, ppkLoading, form, updateCurrentAsset]);

  useEffect(() => {
    const handleWebViewMessage = async (event) => {
      let msg = event.data;
      if (typeof msg === 'string') {
        const trimmed = msg.trim();
        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
          try {
            msg = JSON.parse(trimmed);
          } catch (e) {
            throw new Error('Invalid message from WebView, could not parse JSON: ' + trimmed, e);
          }
        } else return;
      }

      if (msg.type === 'PHOTOS_UPDATE' && msg.screen === CURRENT_SCREEN) {
        // CREATE STANDARDIZED PHOTO OBJECTS
        if (enqueuePhotoUploadRef.current) {
          msg.photos.forEach((photo) => {
            enqueuePhotoUploadRef.current({
              ...createPhotoObject({ ...photo, isExisting: false }),
              base64: photo.base64 || null,
              location: photo.location || null,
            });
          });
        }

        if (msg.photos?.[0]?.location) {
          updateCurrentAsset('lngLat', msg.photos[0].location);
        }
      } else if (msg.type === 'PPK_POSITION_UPDATE' && msg.screen === CURRENT_SCREEN) {
        // Handle PPK position update
        const pos = msg.position;
        if (pos) {
          const latitude = pos.latitude || pos.optDouble?.('latitude');
          const longitude = pos.longitude || pos.optDouble?.('longitude');
          const height = pos.height || pos.optDouble?.('height');

          if (latitude && longitude) {
            updateCurrentAsset('lngLat', [longitude, latitude]);

            // Update form fields with PPK position data
            form.setFieldsValue({
              latitude: latitude,
              longitude: longitude,
              height: height || undefined,
            });

            // Store additional PPK data if needed
            updateCurrentAsset('ppkData', {
              latitude,
              longitude,
              height,
              quality: pos.quality || pos.optInt?.('quality'),
              quality_label: pos.quality_label || pos.optString?.('quality_label', 'Unknown'),
              satellites: pos.satellites || pos.optInt?.('satellites'),
            });

            message.success(
              `PPK position received: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
            );
            setPpkLoading(false);
          }
        }
      } else if (msg.type === 'GEOLOCATION_UPDATE' && msg.screen === CURRENT_SCREEN) {
        // Handle geolocation update
        if (msg.error) {
          message.error(msg.message);
        } else {
          const pos = msg.position;
          if (pos && pos.latitude && pos.longitude) {
            updateCurrentAsset('lngLat', [pos.longitude, pos.latitude]);

            // Update form fields with geolocation data
            form.setFieldsValue({
              latitude: pos.latitude,
              longitude: pos.longitude,
              height: pos.altitude || undefined,
            });

            message.success(
              `GPS position: ${pos.latitude.toFixed(6)}, ${pos.longitude.toFixed(6)}`
            );
            setPpkLoading(false);
          }
        }
      }
    };

    window.addEventListener('message', handleWebViewMessage);
    document.addEventListener('message', handleWebViewMessage);
    return () => {
      window.removeEventListener('message', handleWebViewMessage);
      document.removeEventListener('message', handleWebViewMessage);
    };
  }, [form, updateCurrentAsset]);

  const handleDeletePhoto = async (idx) => {
    const photo = photosMeta[idx];
    if (!photo) return;

    if (photo.status === PHOTO_STATUS.UPLOADING) {
      message.warning('Please wait for the upload to finish before deleting this photo.');
      return;
    }

    deletePhoto(idx);

    if (photo.id && photo.status !== PHOTO_STATUS.ERROR) {
      setPhotosMeta((prev) =>
        prev.map((item, i) => (i === idx ? { ...item, status: PHOTO_STATUS.DELETING } : item))
      );

      try {
        await deleteAssetPhoto({ projectId, photoId: photo.id }).unwrap();
      } catch (error) {
        console.error('Failed to delete uploaded photo', error);
        message.error(error?.data?.error || 'Failed to delete photo. Please try again.');
        setPhotosMeta((prev) =>
          prev.map((item, i) =>
            i === idx ? { ...item, status: PHOTO_STATUS.UPLOADED, errorMessage: null } : item
          )
        );
        return;
      }
    }

    setPhotosMeta((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      updateCurrentAsset('lngLat', updated.length ? updated[0].location || [0, 0] : [0, 0]);
      return updated;
    });
    message.success('Photo removed successfully.');
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();

      if (hasUploadingPhotos) {
        message.warning('Please wait for all photos to finish uploading.');
        return;
      }

      if (hasErroredPhotos) {
        message.error('Please remove or retry photos that failed to upload.');
        return;
      }

      setSubmitting(true);

      const uploadedPhotoIds = photosMeta
        .filter((photo) => photo.status === PHOTO_STATUS.UPLOADED && photo.id)
        .map((photo) => photo.id);

      // Parse numeric fields - use null if empty/undefined
      const finalLat =
        values.latitude !== undefined && values.latitude !== null && values.latitude !== ''
          ? parseFloat(values.latitude)
          : null;
      const finalLng =
        values.longitude !== undefined && values.longitude !== null && values.longitude !== ''
          ? parseFloat(values.longitude)
          : null;
      const finalDepth =
        values.depth !== undefined && values.depth !== null && values.depth !== ''
          ? parseFloat(values.depth)
          : null;
      const finalHeight =
        values.height !== undefined && values.height !== null && values.height !== ''
          ? parseFloat(values.height)
          : null;

      const payload = {
        name: values.name,
        projectId,
        geolocation: finalLng !== null && finalLat !== null ? [finalLng, finalLat] : null,
        latitude: finalLat,
        longitude: finalLng,
        depth: finalDepth,
        altitude: finalHeight,
        installed_at: values.installed_at.toISOString(),
        next_maintenance: values.next_maintenance?.toISOString() || null,
        photo_ids: uploadedPhotoIds,
      };

      console.log('Submitting asset with staged photos:', payload);

      await postAsset({ projectId, asset: payload }).unwrap();
      hasSubmittedRef.current = true;
      form.resetFields();
      setPhotosMeta([]);
      setOpen(false);
      message.success('Asset created successfully');
    } catch (error) {
      console.error('Asset creation failed:', error);
      message.error('Failed to create asset. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    if (!hasSubmittedRef.current) {
      cleanupDraftUploads(photosMetaRef.current);
    }
    form.resetFields();
    setPhotosMeta([]);
    clearPhotos();
    setOpen(false);
  };

  const normFile = (e) => {
    console.log('Upload event:', e);
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const handleDraggerUpload = (file) => {
    try {
      enqueuePhotoUpload({
        file,
        previewUrl: URL.createObjectURL(file),
        fileName: file.name,
        fileType: file.type || 'image/jpeg',
        fileSize: file.size,
        location: currentAsset.lngLat || [0, 0],
      });
      message.success('Photo upload started');
    } catch (error) {
      console.error('Failed to process photo:', error);
      message.error('Failed to process photo');
    }

    return false; // Prevent Upload from uploading automatically
  };

  const handleFieldsChange = () => {
    // Check if there are any form validation errors
    const fieldsError = form.getFieldsError();
    const hasValidationErrors = fieldsError.some((field) => field.errors.length > 0);

    // Also check if required fields are missing values
    const values = form.getFieldsValue();
    const hasMissingRequiredFields = !values.name || !values.installed_at;

    setHasFormErrors(hasValidationErrors || hasMissingRequiredFields);
  };

  return (
    <>
      {/* Mobile DatePicker styles */}
      {isMobile && (
        <style>
          {`
            .mobile-datepicker .ant-picker-footer {
              display: flex !important;
              justify-content: space-between !important;
              align-items: center !important;
              padding: 8px 12px !important;
              border-top: 1px solid #f0f0f0 !important;
              min-height: 48px !important;
              width: 100% !important;
            }
            .mobile-datepicker .ant-picker-footer .ant-btn {
              display: inline-flex !important;
              align-items: center !important;
              justify-content: center !important;
              height: 32px !important;
              min-height: 32px !important;
              padding: 0 16px !important;
              margin: 0 4px !important;
              line-height: 1 !important;
            }
            .mobile-datepicker .ant-picker-footer .ant-picker-now-btn {
              display: inline-flex !important;
              align-items: center !important;
              justify-content: center !important;
              height: 32px !important;
              min-height: 32px !important;
            }
            .mobile-datepicker .ant-picker-footer .ant-picker-ok {
              display: inline-flex !important;
              align-items: center !important;
              justify-content: center !important;
              margin-left: auto !important;
            }
            .mobile-datepicker .ant-picker-footer .ant-picker-ok .ant-btn {
              height: 32px !important;
              min-height: 32px !important;
              display: inline-flex !important;
              align-items: center !important;
              justify-content: center !important;
            }
          `}
        </style>
      )}
      <Modal
        destroyOnClose
        open={open}
        okText="Confirm"
        cancelText="Cancel"
        onCancel={close}
        title="Record Asset"
        onOk={submit}
        okButtonProps={{
          loading: isLoading || submitting,
          disabled: hasUploadingPhotos || submitting || hasFormErrors,
        }}
        closeIcon={isMobile ? null : undefined}
        centered
        style={{
          overflow: 'hidden',
        }}
        styles={{
          body: {
            maxHeight: isMobile ? 'calc(70vh - 120px)' : 'calc(90vh - 120px)',
            overflow: 'hidden',
          },
          header: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 16,
            fontWeight: 600,
            fontSize: 18,
          },
          footer: {
            display: 'flex',
            justifyContent: 'end',
            gap: 16,
            alignItems: 'center',
            padding: 8,
          },
        }}
        width={isMobile ? '90%' : 600}
      >
        <Form
          form={form}
          layout="vertical"
          onFieldsChange={handleFieldsChange}
          style={{
            maxHeight: isMobile ? 'calc(70vh - 120px)' : 'calc(90vh - 120px)',
            overflowY: 'auto',
            padding: 24,
          }}
        >
          <Form.Item
            name="name"
            label="Asset Name"
            rules={[{ required: true, message: 'Please enter asset name' }]}
          >
            <Input placeholder="Enter asset name" />
          </Form.Item>

          <Form.Item
            name="installed_at"
            label="Installed At"
            rules={[{ required: true, message: 'Please select installation date and time' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              inputReadOnly
              allowClear
              showTime={{
                hideDisabledOptions: true,
                defaultValue: null,
              }}
              showNow={true}
              format="YYYY-MM-DD HH:mm:ss"
              popupStyle={
                isMobile
                  ? {
                      maxWidth: '98vw',
                      width: '98vw',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      position: 'fixed',
                      zIndex: 1050,
                    }
                  : {}
              }
              dropdownClassName={isMobile ? 'mobile-datepicker' : ''}
              getPopupContainer={isMobile ? () => document.body : undefined}
              onOk={(value) => {
                form.setFieldsValue({ installed_at: value });
              }}
            />
          </Form.Item>

          <Form.Item name="next_maintenance" label="Next Maintenance">
            <DatePicker
              style={{ width: '100%' }}
              inputReadOnly
              allowClear
              showTime={{
                hideDisabledOptions: true,
                defaultValue: null,
              }}
              showNow={true}
              format="YYYY-MM-DD HH:mm:ss"
              popupStyle={
                isMobile
                  ? {
                      maxWidth: '98vw',
                      width: '98vw',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      position: 'fixed',
                      zIndex: 1050,
                    }
                  : {}
              }
              dropdownClassName={isMobile ? 'mobile-datepicker' : ''}
              getPopupContainer={isMobile ? () => document.body : undefined}
              onOk={(value) => {
                form.setFieldsValue({ next_maintenance: value });
              }}
            />
          </Form.Item>

          <Form.Item name="depth" label="Depth (m)">
            <Input type="number" placeholder="Enter depth in meters" />
          </Form.Item>

          <Form.Item name="latitude" label="Latitude">
            <Input type="number" placeholder="Enter latitude" step="any" />
          </Form.Item>

          <Form.Item name="longitude" label="Longitude">
            <Input type="number" placeholder="Enter longitude" step="any" />
          </Form.Item>

          <Form.Item name="height" label="Height (m)">
            <Input type="number" placeholder="Enter height in meters" step="any" />
          </Form.Item>

          {isMobile ? (
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-evenly',
                  alignItems: 'center',
                  marginBottom: 16,
                }}
              >
                <Button
                  label="Gallery"
                  icon="pi pi-images"
                  raised
                  severity="secondary"
                  onClick={openGallery}
                  disabled={submitting}
                  style={{ flex: '0 1 120px' }}
                />
                <Button
                  label="Camera"
                  icon="pi pi-camera"
                  raised
                  severity="primary"
                  onClick={openCamera}
                  disabled={submitting}
                  style={{ flex: '0 1 120px' }}
                />
              </div>
              {/* Disable the buttons as we are using the PPK from the parent screen */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-evenly',
                  alignItems: 'center',
                }}
              >
                <Button
                  label="Location"
                  icon="pi pi-map-marker"
                  raised
                  severity="secondary"
                  onClick={requestGeolocation}
                  disabled={submitting}
                  style={{ flex: '0 1 120px' }}
                />
                <Button
                  label="PPK"
                  icon="pi pi-wifi"
                  raised
                  severity="primary"
                  onClick={requestPPK}
                  disabled={submitting || ppkLoading || parentPpkLoading}
                  loading={ppkLoading || parentPpkLoading}
                  style={{ flex: '0 1 120px' }}
                />
              </div>
            </div>
          ) : (
            <Form.Item label="Photo">
              <Form.Item
                name="dragger"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                noStyle
              >
                <Upload.Dragger
                  maxCount={1}
                  beforeUpload={handleDraggerUpload}
                  accept=".png,.jpg,.jpeg"
                  showUploadList={false}
                  name="photo"
                  disabled={submitting}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    {submitting ? 'Processing...' : 'Click or drag photo to this area to upload'}
                  </p>
                  <p className="ant-upload-hint">Acceptable extensions are .png, .jpg and .jpeg</p>
                </Upload.Dragger>
              </Form.Item>
            </Form.Item>
          )}

          {submitting && (
            <div style={{ textAlign: 'center', padding: 16 }}>
              <p>Uploading Asset...</p>
            </div>
          )}

          {/* PHOTO GRID - Only show when there are photos */}
          {photosMeta.length > 0 && (
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: 8,
                border: '1px solid #f0f0f0',
                borderRadius: 4,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: 12,
                marginBottom: isMobile ? 80 : 0, // Add bottom margin on mobile to account for navbar
              }}
            >
              {photosMeta.map((photo, idx) => {
                const statusMeta = STATUS_STYLES[photo.status];
                return (
                  <div
                    key={photo.id || photo.tempId || idx}
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: 150,
                      backgroundColor: '#fafafa',
                      borderRadius: 6,
                      border: '1px solid #ddd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {statusMeta && (
                      <Tag
                        color={statusMeta.color}
                        style={{ position: 'absolute', top: 8, left: 8, zIndex: 10 }}
                      >
                        {statusMeta.label}
                      </Tag>
                    )}
                    {photo.status === PHOTO_STATUS.ERROR && (
                      <Button
                        label="Retry"
                        text
                        size="small"
                        severity="warning"
                        onClick={() => retryPhotoUpload(photo.tempId)}
                        style={{ position: 'absolute', bottom: 8, left: 8, zIndex: 10 }}
                        disabled={submitting}
                      />
                    )}
                    <Image
                      src={getPhotoDisplayUrl(photo)}
                      preview
                      imageStyle={{
                        height: 148,
                        width: '100%',
                        objectFit: 'cover',
                        borderRadius: 6,
                        padding: 1,
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        maxWidth: '100%',
                        height: 148,
                      }}
                    />
                    <Tooltip title="Delete photo">
                      <Button
                        icon="pi pi-trash"
                        text
                        size="small"
                        shape="circle"
                        onClick={() => handleDeletePhoto(idx)}
                        style={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}
                        disabled={
                          submitting ||
                          photo.status === PHOTO_STATUS.DELETING ||
                          photo.status === PHOTO_STATUS.UPLOADING
                        }
                      />
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          )}
        </Form>
      </Modal>
    </>
  );
}
